import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Eye, FileText, Pencil, Save } from "lucide-react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { renderMarkdown } from "@/lib/markdown";
import { toast } from "sonner";

/**
 * Phase L — Platform admin editor for the Terms of Service and Privacy
 * Policy documents.
 *
 * UX:
 *  - Top-level tabs: "Terms of Service" / "Privacy Policy".
 *  - Within each, sub-tabs: "English (en)" / "Spanish (es)" — a textarea per
 *    language, plus a preview tab that runs the same markdown renderer the
 *    public /terms and /privacy pages use.
 *  - Save button posts both languages at once and the backend auto-bumps the
 *    `version`. The card refreshes from the server so the version badge
 *    updates immediately.
 *
 * Re-consent: every save triggers the version bump that makes existing users
 * see the re-consent modal on next login. We surface a warning callout above
 * the save button so the operator can't miss this consequence.
 */
function DocEditor({ kind }) {
  const { t } = useTranslation();
  const [doc, setDoc] = useState(null);   // {version, content_en, content_es, ...}
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [contentEn, setContentEn] = useState("");
  const [contentEs, setContentEs] = useState("");
  const [langTab, setLangTab] = useState("en");
  const [mode, setMode] = useState("edit");  // edit | preview

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/platform/legal/${kind}`);
      setDoc(r.data);
      setContentEn(r.data.content_en || "");
      setContentEs(r.data.content_es || "");
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("legal.fetchFailed", "Could not load document."));
    } finally {
      setLoading(false);
    }
  }, [kind, t]);

  useEffect(() => {
    reload();
  }, [reload]);

  const onSave = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/platform/legal/${kind}`, {
        content_en: contentEn,
        content_es: contentEs,
      });
      toast.success(t("legal.admin.saved", "Saved — version bumped to v{{version}}.", { version: r.data.version }));
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("legal.admin.saveFailed", "Save failed."));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-3" data-testid={`legal-admin-loading-${kind}`}>
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-9 w-32" />
      </div>
    );
  }

  const previewSrc = langTab === "es" ? contentEs : contentEn;

  return (
    <div className="space-y-3" data-testid={`legal-admin-editor-${kind}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Badge variant="outline" className="font-mono" data-testid={`legal-admin-version-${kind}`}>
            v{doc?.version ?? "?"}
          </Badge>
          {doc?.updated_at && (
            <span>
              {t("legal.admin.updatedAt", "Last updated")}:{" "}
              {new Date(doc.updated_at).toLocaleString()}
            </span>
          )}
          {doc?.updated_by && (
            <span>· {t("legal.admin.updatedBy", "Updated by")} {doc.updated_by}</span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <Button
            type="button"
            size="sm"
            variant={mode === "edit" ? "default" : "outline"}
            onClick={() => setMode("edit")}
            data-testid={`legal-admin-mode-edit-${kind}`}
          >
            <Pencil className="h-3.5 w-3.5 mr-1" />
            {t("legal.admin.edit", "Edit")}
          </Button>
          <Button
            type="button"
            size="sm"
            variant={mode === "preview" ? "default" : "outline"}
            onClick={() => setMode("preview")}
            data-testid={`legal-admin-mode-preview-${kind}`}
          >
            <Eye className="h-3.5 w-3.5 mr-1" />
            {t("legal.admin.preview", "Preview")}
          </Button>
        </div>
      </div>

      <Tabs value={langTab} onValueChange={setLangTab}>
        <TabsList className="grid grid-cols-2 w-full max-w-md">
          <TabsTrigger value="en" data-testid={`legal-admin-tab-en-${kind}`}>
            {t("legal.admin.english", "English (en)")}
          </TabsTrigger>
          <TabsTrigger value="es" data-testid={`legal-admin-tab-es-${kind}`}>
            {t("legal.admin.spanish", "Spanish (es)")}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="en" className="mt-2">
          {mode === "edit" ? (
            <Textarea
              value={contentEn}
              onChange={(e) => setContentEn(e.target.value)}
              rows={18}
              className="font-mono text-xs"
              spellCheck={false}
              data-testid={`legal-admin-textarea-en-${kind}`}
            />
          ) : (
            <article
              className="prose prose-sm max-w-none rounded-md border border-border p-4 bg-background min-h-[20vh]"
              data-testid={`legal-admin-preview-en-${kind}`}
            >
              {renderMarkdown(contentEn)}
            </article>
          )}
        </TabsContent>
        <TabsContent value="es" className="mt-2">
          {mode === "edit" ? (
            <Textarea
              value={contentEs}
              onChange={(e) => setContentEs(e.target.value)}
              rows={18}
              className="font-mono text-xs"
              spellCheck={false}
              data-testid={`legal-admin-textarea-es-${kind}`}
            />
          ) : (
            <article
              className="prose prose-sm max-w-none rounded-md border border-border p-4 bg-background min-h-[20vh]"
              data-testid={`legal-admin-preview-es-${kind}`}
            >
              {renderMarkdown(contentEs)}
            </article>
          )}
        </TabsContent>
      </Tabs>

      <div className="rounded-md border border-[hsl(var(--warn))]/40 bg-[hsl(var(--warn))]/10 p-3 flex items-start gap-2 text-xs">
        <AlertTriangle className="h-4 w-4 text-[hsl(var(--warn))] mt-0.5 shrink-0" />
        <span>{t("legal.admin.warning", "Saving these changes will require every existing user to re-accept on next login. Make sure your wording is final.")}</span>
      </div>

      <div>
        <Button
          type="button"
          onClick={onSave}
          disabled={saving}
          data-testid={`legal-admin-save-${kind}`}
        >
          <Save className="h-4 w-4 mr-1" />
          {saving
            ? t("legal.submitting", "Recording…")
            : t("legal.admin.save", "Save & bump version")}
        </Button>
      </div>
    </div>
  );
}

export function LegalEditorCard() {
  const { t } = useTranslation();
  const [kindTab, setKindTab] = useState("terms");
  return (
    <Card className="max-w-3xl" data-testid="card-legal-editor">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-start gap-3">
          <div className="rounded-md bg-primary/10 p-2 mt-0.5">
            <FileText className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h3 className="text-base font-semibold">
              {t("legal.admin.title", "Legal documents")}
            </h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              {t("legal.admin.subtitle", "Edit the Terms of Service and Privacy Policy that public visitors see at /terms and /privacy. Saving bumps the version and forces existing users to re-accept on next login.")}
            </p>
          </div>
        </div>

        <Tabs value={kindTab} onValueChange={setKindTab}>
          <TabsList className="grid grid-cols-2 w-full max-w-md">
            <TabsTrigger value="terms" data-testid="legal-admin-kind-terms">
              {t("legal.admin.tabTerms", "Terms of Service")}
            </TabsTrigger>
            <TabsTrigger value="privacy" data-testid="legal-admin-kind-privacy">
              {t("legal.admin.tabPrivacy", "Privacy Policy")}
            </TabsTrigger>
          </TabsList>
          <TabsContent value="terms" className="mt-3">
            <DocEditor kind="terms" />
          </TabsContent>
          <TabsContent value="privacy" className="mt-3">
            <DocEditor kind="privacy" />
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
